/**
 * Booking Timeline Page — FE-WIRING-1A
 * ======================================
 *
 * Patient view of their treatment journey timeline.
 *
 * AUTHORITY (FE-CONTRACT-LOCK):
 *   - Sole authority is the canonical operational chain
 *     `treatment_cases → milestone_instances` (COM-1D.D).
 *   - Reads come exclusively from `GET /api/cases/person/{patient_id}`
 *     and `GET /api/milestone-instances/case/{case_id}` via the
 *     `useMilestoneTimeline` server-state hook.
 *   - This page no longer consumes the sessionStorage-backed
 *     `TimelineContext`. TimelineContext remains quarantined (R-11)
 *     and is not imported here.
 *
 * R-13 (Patient FORBIDDEN on operational tasks):
 *   This page does NOT render `milestone_tasks` (no checklist, no
 *   per-task UI). The patient may only see milestone-level progress.
 */
import React, { useMemo, useState } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTravel } from '../../context/TravelContext';
import { useAuth } from '../../context/AuthContext';
import CalendarWidget from '../../components/CalendarWidget';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft, Calendar, CheckCircle, Circle, Clock,
  ChevronDown, ChevronUp, MapPin, FileText, Plane, MessageSquare,
  AlertCircle, Loader2
} from 'lucide-react';
import { Toaster } from '../../components/Toaster';
import {
  useMilestoneTimeline,
  MILESTONE_STATUS_DISPLAY,
  computeMilestoneProgress,
  pickCurrentMilestone,
} from '../../hooks/useMilestoneTimeline';

const BookingTimeline = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Patient ID from auth context (server resolves the canonical case_id).
  const patientId = user?.id || user?.patient_id || '';

  const {
    loading,
    error,
    caseId,
    milestones,
  } = useMilestoneTimeline({ patientId });

  const progress = useMemo(() => computeMilestoneProgress(milestones), [milestones]);
  const currentMilestone = useMemo(() => pickCurrentMilestone(milestones), [milestones]);

  const [expandedIds, setExpandedIds] = useState([]);

  // Default-expand the current milestone once it resolves.
  React.useEffect(() => {
    if (currentMilestone && currentMilestone.id) {
      setExpandedIds((prev) => (prev.includes(currentMilestone.id) ? prev : [currentMilestone.id]));
    }
  }, [currentMilestone]);

  const toggle = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Calendar events derived from canonical milestones (scheduled_date alias).
  const calendarEvents = useMemo(
    () =>
      (milestones || [])
        .filter((m) => m && (m.scheduledDate || m.date))
        .map((m) => ({
          title: m.title || `Step ${m.step ?? ''}`.trim(),
          date: m.scheduledDate || m.date,
          status: m.status,
          type: 'milestone',
        })),
    [milestones]
  );

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center"
        data-testid="booking-timeline-loading"
      >
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-spin" />
            <p className="text-slate-600">{t('patient.common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center"
        data-testid="booking-timeline-error"
      >
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">{t('patient.common.error')}</h2>
            <p className="text-slate-600 mb-4">
              {t('patient.common.error')}
            </p>
            <Button onClick={() => navigate('/patient/dashboard')} data-testid="booking-timeline-error-back">
              {t('patient.shell.goToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Empty (no active case yet) ──────────────────────────────────────────
  if (!caseId || !milestones || milestones.length === 0) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center"
        data-testid="booking-timeline-empty"
      >
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">{t('patient.timeline.empty').split('.')[0]}</h2>
            <p className="text-slate-600 mb-4">
              {t('patient.timeline.empty')}
            </p>
            <Button onClick={() => navigate('/patient/dashboard')} data-testid="booking-timeline-empty-back">
              {t('patient.shell.goToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loaded ──────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50"
      data-testid="booking-timeline-page"
    >
      <Toaster />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/80 hover:text-white mb-4"
            data-testid="booking-timeline-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('patient.common.back')}
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <Calendar className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">{t('patient.timeline.title')}</h1>
              <p className="text-blue-100 mt-1">
                {t('patient.timeline.subtitle')}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-8" data-testid="booking-timeline-progress-wrap">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-100">{t('patient.journey.title')}</span>
              <span className="text-sm font-semibold" data-testid="booking-timeline-progress-value">
                {progress}%
              </span>
            </div>
            <Progress value={progress} className="h-3 bg-white/20" />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Timeline */}
          <div className="lg:col-span-2 space-y-4" data-testid="booking-timeline-list">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Timeline Steps</h2>

            {milestones.map((m) => {
              const id = m.id || m.milestone_instance_id;
              const isExpanded = expandedIds.includes(id);
              const isCurrent = currentMilestone && currentMilestone.id === id;
              const display = MILESTONE_STATUS_DISPLAY[m.status] ||
                MILESTONE_STATUS_DISPLAY.pending;
              const scheduled = m.scheduledDate || m.date;

              return (
                <Card
                  key={id}
                  className={`transition-all ${isCurrent ? 'ring-2 ring-blue-500 shadow-lg' : ''} ${
                    m.status === 'achieved' ? 'bg-green-50/50' : ''
                  }`}
                  data-testid={`booking-timeline-step-${id}`}
                >
                  <CardContent className="p-0">
                    {/* Step Header */}
                    <div
                      className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => toggle(id)}
                      data-testid={`booking-timeline-step-toggle-${id}`}
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          m.status === 'achieved'
                            ? 'bg-green-500'
                            : m.status === 'in_progress'
                            ? 'bg-blue-100'
                            : 'bg-slate-100'
                        }`}
                      >
                        {m.status === 'achieved' ? (
                          <CheckCircle className="h-6 w-6 text-white" />
                        ) : (
                          <Circle
                            className={`h-6 w-6 ${
                              m.status === 'in_progress' ? 'text-blue-600' : 'text-slate-400'
                            }`}
                          />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            Step {m.step ?? '—'}
                          </span>
                          {isCurrent && (
                            <Badge className="bg-blue-100 text-blue-700">Current</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-800">
                          {m.title || 'Untitled milestone'}
                        </h3>
                        {scheduled && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateLocale(scheduled)}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className={display.badgeClass}
                          data-testid={`booking-timeline-step-status-${id}`}
                        >
                          {display.label}
                        </Badge>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded content — milestone-level only (R-13: no tasks for patient) */}
                    {isExpanded && (
                      <div
                        className="px-4 pb-4 border-t bg-slate-50/50"
                        data-testid={`booking-timeline-step-details-${id}`}
                      >
                        {m.description ? (
                          <p className="text-slate-600 mt-4">{m.description}</p>
                        ) : (
                          <p className="text-slate-400 mt-4 italic">
                            No additional details provided for this milestone.
                          </p>
                        )}

                        {m.completedAt && (
                          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Completed on{' '}
                            {formatDateLocale(m.completedAt)}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <CalendarWidget events={calendarEvents} title="Key Dates" />

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/patient/documents')}
                  data-testid="booking-timeline-action-documents"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Documents
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/patient/travel-details')}
                  data-testid="booking-timeline-action-travel"
                >
                  <Plane className="h-4 w-4 mr-2" />
                  Travel Details
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/patient/messages')}
                  data-testid="booking-timeline-action-messages"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Messages
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Case Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">Treatment case</p>
                    <p className="text-xs text-slate-500 truncate" data-testid="booking-timeline-case-id">
                      {caseId}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingTimeline;
